import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Search,
    Filter,
    Mail,
    MessageSquare,
    Paperclip,
    ChevronRight,
    CheckCircle,
    Clock,
    User,
    Eye,
    Send,
    X
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import api from '../utils/api';

const MessagesList = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, pending, replied
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMessage, setSelectedMessage] = useState(null); // For details view (modal/panel)

    // Reply State
    const [isReplying, setIsReplying] = useState(false);
    const [replySubject, setReplySubject] = useState('');
    const [replyMessage, setReplyMessage] = useState('');
    const [sendingReply, setSendingReply] = useState(false);

    // Fetch Messages
    const fetchMessages = async () => {
        try {
            const response = await api.get(`/contact`);
            if (response.data.success) {
                setMessages(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
            toast.error('Impossible de charger les messages');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMessages();

        // Socket.io integration
        import('socket.io-client').then(({ io }) => {
            const socket = io(process.env.REACT_APP_BASE_URL);

            socket.on('connect', () => {
                socket.emit('join_admin_room');
            });

            socket.on('new_contact_message', (newMessage) => {
                setMessages(prev => [newMessage, ...prev]);
                toast.success('Nouveau message reçu !', {
                    icon: '🔔',
                    duration: 5000
                });
            });

            return () => {
                socket.disconnect();
            };
        });

    }, []);

    // mark as replied
    const handleMarkAsReplied = async (id, currentStatus) => {
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`${process.env.REACT_APP_BASE_URL}/contact/${id}/status`,
                { status: currentStatus === 'replied' ? 'pending' : 'replied' },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setMessages(prev => prev.map(msg =>
                msg._id === id ? { ...msg, status: currentStatus === 'replied' ? 'pending' : 'replied' } : msg
            ));

            // Update selected message if it's the one being modified
            if (selectedMessage && selectedMessage._id === id) {
                setSelectedMessage(prev => ({ ...prev, status: currentStatus === 'replied' ? 'pending' : 'replied' }));
            }

            toast.success('Statut mis à jour');
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const handleSendReply = async (e) => {
        e.preventDefault();
        if (!replyMessage.trim()) return;

        setSendingReply(true);
        try {
            const response = await api.post(`/contact/${selectedMessage._id}/reply`, {
                subject: replySubject,
                message: replyMessage
            });

            toast.success('Réponse envoyée avec succès');

            // Update message status to replied
            setMessages(prev => prev.map(msg =>
                msg._id === selectedMessage._id ? { ...msg, status: 'replied' } : msg
            ));

            if (selectedMessage) {
                setSelectedMessage(prev => ({ ...prev, status: 'replied' }));
            }

            // Reset and close reply
            setIsReplying(false);
            setReplyMessage('');
            setReplySubject('');

        } catch (error) {
            console.error(error);
            toast.error('Erreur lors de l\'envoi de la réponse');
        } finally {
            setSendingReply(false);
        }
    };

    const openAttachment = async (attachmentPath) => {
        try {
            const response = await api.get(`/files/${attachmentPath}`, {
                responseType: 'blob',
            });
            const blobUrl = URL.createObjectURL(response.data);
            window.open(blobUrl, '_blank');
            // Revoke after a short delay to free memory
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } catch (error) {
            console.error('Error opening attachment:', error);
            toast.error('Impossible d\'ouvrir la pièce jointe');
        }
    };

    const openReply = () => {
        setReplySubject(`Re: ${selectedMessage.subject}`);
        setIsReplying(true);
    };

    const filteredMessages = messages.filter(msg => {
        const matchesFilter = filter === 'all' ? true : msg.status === filter;
        const matchesSearch =
            msg.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
            msg.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (msg.sender?.first_name + ' ' + msg.sender?.last_name).toLowerCase().includes(searchTerm.toLowerCase());

        return matchesFilter && matchesSearch;
    });

    const getStatusColor = (status) => {
        return status === 'replied'
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    };

    const getSenderBadgeStyle = (model) => {
        if (model === 'Visitor') return 'bg-purple-50 text-purple-700 border-purple-200';
        if (model === 'Candidate') return 'bg-yellow-50 text-yellow-700 border-yellow-200';
        return 'bg-blue-50 text-blue-700 border-blue-200';
    };


    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Messages</h1>
                <p className="text-slate-500">Gérez les demandes de contact des utilisateurs et visiteurs.</p>
            </div>

            {/* Filters & Actions */}
            <div className="flex flex-col sm:flex-row justify-between gap-4 bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                <div className="flex items-center gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Rechercher..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border-none rounded-lg text-sm focus:ring-2 focus:ring-yellow-500"
                        />
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <div className="flex p-1 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        {['all', 'pending', 'replied'].map((f) => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${filter === f
                                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                    }`}
                            >
                                {f === 'all' ? 'Tous' : f === 'pending' ? 'En attente' : 'Répondu'}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* List */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-yellow-500 border-t-transparent mx-auto"></div>
                        <p className="mt-4 text-slate-500">Chargement des messages...</p>
                    </div>
                ) : filteredMessages.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Mail className="w-8 h-8 text-slate-400" />
                        </div>
                        <h3 className="text-lg font-medium text-slate-900 dark:text-white">Aucun message trouvé</h3>
                        <p className="text-slate-500">Il n'y a pas de messages correspondant à vos critères.</p>
                    </div>
                ) : (
                    <div className="divide-y divide-slate-200 dark:divide-slate-800">
                        {filteredMessages.map((msg) => (
                            <motion.div
                                key={msg._id}
                                layout
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="group p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                                onClick={() => { setSelectedMessage(msg); setIsReplying(false); }}
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-start gap-4">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${msg.senderModel === 'Visitor' ? 'bg-purple-100 text-purple-600' :
                                            msg.senderModel === 'Recruiter' ? 'bg-blue-100 text-blue-600' :
                                                'bg-yellow-100 text-yellow-600'
                                            }`}>
                                            {msg.senderModel === 'Visitor' ? <User className="w-5 h-5" /> :
                                                msg.senderModel === 'Recruiter' ? <div className="font-bold">R</div> :
                                                    <div className="font-bold">C</div>}
                                        </div>

                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h4 className="font-semibold text-slate-900 dark:text-white">
                                                    {msg.senderModel === 'Visitor'
                                                        ? msg.name
                                                        : msg.sender
                                                            ? (msg.senderModel === 'Candidate'
                                                                ? `${msg.sender.first_name} ${msg.sender.last_name}`
                                                                : msg.senderModel === 'Recruiter'
                                                                    ? msg.sender.company_name
                                                                    : 'Utilisateur Inconnu')
                                                            : 'Utilisateur Inconnu'
                                                    }
                                                </h4>
                                                <span className={`text-xs px-2 py-0.5 rounded-full border ${getSenderBadgeStyle(msg.senderModel)}`}>
                                                    {msg.senderModel}
                                                </span>
                                            </div>
                                            <div className="text-sm text-slate-500 flex items-center gap-2 mt-0.5">
                                                <Mail className="w-3 h-3" />
                                                {msg.email || msg.sender?.email}
                                            </div>
                                            <h5 className="font-medium text-slate-800 dark:text-slate-200 mt-2">
                                                {msg.subject}
                                            </h5>
                                            <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mt-1">
                                                {msg.message}
                                            </p>

                                            {msg.attachment && (
                                                <div className="flex items-center gap-1 text-xs text-blue-600 mt-2 bg-blue-50 w-fit px-2 py-1 rounded">
                                                    <Paperclip className="w-3 h-3" />
                                                    Pièce jointe disponible
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex flex-col items-end gap-2">
                                        <div className="text-xs text-slate-400 whitespace-nowrap">
                                            {format(new Date(msg.createdAt), 'dd MMM yyyy HH:mm', { locale: fr })}
                                        </div>
                                        <div className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium ${getStatusColor(msg.status)}`}>
                                            {msg.status === 'pending' ? <Clock className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                                            {msg.status === 'pending' ? 'En attente' : 'Répondu'}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>

            {/* Details Modal */}
            <AnimatePresence>
                {selectedMessage && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
                        >
                            <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex justify-between items-start bg-slate-50 dark:bg-slate-950">
                                <div>
                                    <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                        <MessageSquare className="w-5 h-5 text-yellow-500" />
                                        Détails du message
                                    </h3>
                                    <p className="text-sm text-slate-500 mt-1">
                                        Reçu le {format(new Date(selectedMessage.createdAt), 'PPP à HH:mm', { locale: fr })}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setSelectedMessage(null)}
                                    className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-6 overflow-y-auto">
                                <div className="space-y-6">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                                            <label className="text-xs font-semibold text-slate-400 uppercase">Expéditeur</label>
                                            <p className="font-medium text-slate-900 dark:text-white">
                                                {selectedMessage.senderModel === 'Visitor' ? selectedMessage.name :
                                                    selectedMessage.sender ? (
                                                        selectedMessage.senderModel === 'Candidate'
                                                            ? `${selectedMessage.sender.first_name} ${selectedMessage.sender.last_name}`
                                                            : selectedMessage.sender.company_name
                                                    ) : 'N/A'}
                                            </p>
                                            <p className="text-sm text-slate-500">{selectedMessage.senderModel}</p>
                                        </div>
                                        <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                                            <label className="text-xs font-semibold text-slate-400 uppercase">Contact</label>
                                            <p className="font-medium text-slate-900 dark:text-white">
                                                {selectedMessage.email || selectedMessage.sender?.email}
                                            </p>
                                            <a href={`mailto:${selectedMessage.email || selectedMessage.sender?.email}`} className="text-sm text-blue-500 hover:underline">
                                                Envoyer un email
                                            </a>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-xs font-semibold text-slate-400 uppercase">Sujet</label>
                                        <p className="text-lg font-medium text-slate-900 dark:text-white">{selectedMessage.subject}</p>
                                    </div>

                                    <div>
                                        <label className="text-xs font-semibold text-slate-400 uppercase">Message</label>
                                        <div className="mt-2 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                                            {selectedMessage.message}
                                        </div>
                                    </div>

                                    {selectedMessage.attachment && (
                                        <div>
                                            <label className="text-xs font-semibold text-slate-400 uppercase">Pièce jointe</label>
                                            <button
                                                onClick={() => openAttachment(selectedMessage.attachment)}
                                                className="mt-2 w-full flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors group text-left"
                                            >
                                                <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                                    <Paperclip className="w-5 h-5" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-slate-900 dark:text-white">Voir le fichier</p>
                                                    <p className="text-xs text-slate-500">Cliquez pour ouvrir</p>
                                                </div>
                                                <Eye className="w-4 h-4 text-slate-400 group-hover:text-blue-500" />
                                            </button>
                                        </div>
                                    )}

                                    {/* Reply Section */}
                                    {isReplying && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-800"
                                        >
                                            <h4 className="text-sm font-bold text-slate-900 dark:text-white mb-4">Votre réponse</h4>
                                            <form onSubmit={handleSendReply} className="space-y-4">
                                                <div>
                                                    <label className="text-xs font-semibold text-slate-400 uppercase mb-1 block">Sujet</label>
                                                    <input
                                                        type="text"
                                                        value={replySubject}
                                                        onChange={e => setReplySubject(e.target.value)}
                                                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-2 focus:ring-blue-500 outline-none"
                                                        placeholder="Sujet de la réponse"
                                                        required
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-semibold text-slate-400 uppercase mb-1 block">Message</label>
                                                    <textarea
                                                        value={replyMessage}
                                                        onChange={e => setReplyMessage(e.target.value)}
                                                        rows={6}
                                                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                                                        placeholder="Écrivez votre réponse ici..."
                                                        required
                                                    />
                                                </div>
                                                <div className="flex justify-end gap-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsReplying(false)}
                                                        className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
                                                    >
                                                        Annuler
                                                    </button>
                                                    <button
                                                        type="submit"
                                                        disabled={sendingReply}
                                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                                                    >
                                                        {sendingReply ? (
                                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                        ) : (
                                                            <Send className="w-4 h-4" />
                                                        )}
                                                        Envoyer
                                                    </button>
                                                </div>
                                            </form>
                                        </motion.div>
                                    )}
                                </div>
                            </div>

                            {!isReplying && (
                                <div className="p-6 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex justify-end gap-3">
                                    <button
                                        onClick={() => setSelectedMessage(null)}
                                        className="px-4 py-2 text-slate-600 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                    >
                                        Fermer
                                    </button>
                                    <button
                                        onClick={() => handleMarkAsReplied(selectedMessage._id, selectedMessage.status)}
                                        className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${selectedMessage.status === 'replied'
                                            ? 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                                            : 'bg-green-600 text-white hover:bg-green-700'
                                            }`}
                                    >
                                        {selectedMessage.status === 'replied' ? 'Marquer comme non répondu' : 'Marquer comme répondu'}
                                        {selectedMessage.status !== 'replied' && <CheckCircle className="w-4 h-4" />}
                                    </button>
                                    <button
                                        onClick={openReply}
                                        className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-medium transition-colors flex items-center gap-2"
                                    >
                                        <Mail className="w-4 h-4" />
                                        Répondre
                                    </button>
                                </div>
                            )}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default MessagesList;
